import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useVault } from "@/context/VaultContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { PasswordField } from "./PasswordField";
import { StrengthMeter } from "./StrengthMeter";
import { RecoveryCodeCard } from "./RecoveryCodeCard";
import { passwordStrength } from "@/lib/vaultCrypto";
import { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Lock, KeyRound, LifeBuoy, Loader2, ArrowRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";

const STEPS = ["Om hvelvet", "Hovedpassord", "Gjenoppretting"];

export const SetupWizard = ({ onDone }) => {
  const { user } = useAuth();
  const { setup, busy } = useVault();
  const [step, setStep] = useState(0);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [ack, setAck] = useState(false);
  const [error, setError] = useState("");
  const [recoveryCode, setRecoveryCode] = useState(null);
  const strength = passwordStrength(pw);
  const okStrength = pw.length >= 12 && strength.score >= 2;
  const match = pw && pw === pw2;

  const create = async (e) => {
    e.preventDefault();
    setError("");
    if (!okStrength) { setError("Hovedpassordet må være minst 12 tegn og av middels styrke eller bedre."); return; }
    if (!match) { setError("Passordene er ikke like."); return; }
    try {
      const code = await setup(pw);
      setRecoveryCode(code);
      setPw(""); setPw2("");
      setStep(2);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <div className="mx-auto max-w-2xl" data-testid="vault-setup-wizard">
      <ol className="flex items-center gap-2 mb-6 text-sm" aria-label="Steg">
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-2">
            <span className={cn("h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold", i < step ? "bg-primary text-primary-foreground" : i === step ? "bg-accent text-accent-foreground ring-2 ring-primary/40" : "bg-secondary text-muted-foreground")}>{i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}</span>
            <span className={cn("hidden sm:inline", i === step ? "font-medium" : "text-muted-foreground")}>{s}</span>
            {i < STEPS.length - 1 ? <span className="w-6 sm:w-10 h-px bg-border" /> : null}
          </li>
        ))}
      </ol>

      {step === 0 ? (
        <Card>
          <CardHeader>
            <div className="h-12 w-12 rounded-xl bg-accent text-accent-foreground flex items-center justify-center mb-2"><KeyRound className="h-6 w-6" /></div>
            <CardTitle>Sett opp passordhvelvet</CardTitle>
            <CardDescription>Ett trygt sted for alle pålogginger – med hurtigknapp rett inn i systemene du bruker.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-3 text-sm">
              {[
                [ShieldCheck, "Nullkunnskap", "Alt krypteres i nettleseren din med et hovedpassord bare du kjenner. Controlle-serveren lagrer kun kryptert innhold og kan aldri lese passordene dine."],
                [KeyRound, "Du velger hovedpassordet selv", "I neste steg skriver du inn et hovedpassord du bestemmer selv. Det sendes aldri til deg på e-post eller SMS – husk det, eller lagre det i en annen passordløsning."],
                [Lock, "Låser seg selv", "Hvelvet låses automatisk etter noen minutter uten aktivitet, og alltid når du logger ut."],
                [LifeBuoy, "Gjenopprettingskode", "Du får en kode som kan låse opp hvelvet hvis du glemmer hovedpassordet. Uten hovedpassord eller kode kan ingen – heller ikke vi – gjenopprette innholdet, men du kan alltid nullstille hvelvet og starte på nytt."],
              ].map(([Icon, t, d]) => (
                <li key={t} className="flex gap-3"><Icon className="h-5 w-5 text-primary shrink-0 mt-0.5" /><div><p className="font-medium">{t}</p><p className="text-muted-foreground">{d}</p></div></li>
              ))}
            </ul>
            <label className="flex items-start gap-3 rounded-lg border p-3 text-sm cursor-pointer">
              <input type="checkbox" className="mt-1 h-4 w-4 accent-[hsl(var(--primary))]" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="setup-ack-checkbox" />
              <span>Jeg forstår at et glemt hovedpassord uten gjenopprettingskode betyr at innholdet er tapt for alltid.</span>
            </label>
            <Button className="w-full" disabled={!ack} onClick={() => setStep(1)} data-testid="setup-next-button">Velg hovedpassord <ArrowRight className="h-4 w-4 ml-2" /></Button>
          </CardContent>
        </Card>
      ) : null}

      {step === 1 ? (
        <Card>
          <CardHeader>
            <CardTitle>Velg hovedpassord</CardTitle>
            <CardDescription>Dette er passordet du selv bestemmer og bruker for å låse opp hvelvet – det sendes ikke til deg. Bruk gjerne en setning på 4–5 ord du husker, f.eks. «blå kaffe seiler over fjellet». Minst 12 tegn.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={create} className="space-y-5" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="master">Hovedpassord</Label>
                <PasswordField id="master" value={pw} onChange={(e) => setPw(e.target.value)} testId="setup-master" sensitive={false} />
                <StrengthMeter password={pw} testId="setup-strength" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="master2">Gjenta hovedpassord</Label>
                <PasswordField id="master2" value={pw2} onChange={(e) => setPw2(e.target.value)} testId="setup-master-repeat" sensitive={false} />
                {pw2 && !match ? <p className="text-xs text-destructive" data-testid="setup-mismatch">Passordene er ikke like.</p> : null}
              </div>
              {error ? <Alert variant="destructive" data-testid="setup-error"><AlertTitle>Kunne ikke opprette hvelvet</AlertTitle><AlertDescription>{error}</AlertDescription></Alert> : null}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setStep(0)} disabled={busy}>Tilbake</Button>
                <Button type="submit" className="flex-1" disabled={busy || !okStrength || !match} data-testid="setup-create-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />} {busy ? "Utleder nøkler …" : "Opprett hvelv"}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}

      {step === 2 && recoveryCode ? (
        <Card>
          <CardHeader>
            <CardTitle>Lagre gjenopprettingskoden</CardTitle>
            <CardDescription>Dette er eneste gang koden vises. Skriv den ned eller lagre den på et trygt sted – adskilt fra hovedpassordet.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert data-testid="setup-master-reminder">
              <KeyRound className="h-4 w-4" />
              <AlertTitle>Husk: hovedpassordet du nettopp valgte er nøkkelen</AlertTitle>
              <AlertDescription>Neste gang låser du opp med hovedpassordet fra forrige steg. Koden under er kun en reserve hvis du glemmer det – ingen av dem sendes til deg på e-post.</AlertDescription>
            </Alert>
            <RecoveryCodeCard code={recoveryCode} userEmail={user?.email} onConfirmed={() => { setRecoveryCode(null); toast.success("Hvelvet er klart"); onDone?.(); }} />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};
